import { useState, useEffect } from 'react';
import { useCustomersPG } from './useCustomersPG';
import { useServicesPG } from './useServicesPG';
import { useAppointmentsPG } from './useAppointmentsPG';

// Hook for dashboard-specific data with individual loading states
export function useDashboard() {
  const appointments = useAppointmentsPG();
  const customers = useCustomersPG();
  const services = useServicesPG();

  // Separate loading states for each section
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);

  // Load dashboard metrics on mount
  useEffect(() => {
    const loadMetrics = async () => {
      try {
        setMetricsLoading(true);
        setMetricsError(null);
        const dashboardMetrics = await appointments.getDashboardMetrics();
        setMetrics(dashboardMetrics);
      } catch (error) {
        console.error('Failed to load dashboard metrics:', error);
        setMetricsError(error.message);
      } finally {
        setMetricsLoading(false);
      }
    };

    // Only load metrics if getDashboardMetrics is available
    if (appointments.getDashboardMetrics && !appointments.loading) {
      loadMetrics();
    }
  }, [appointments.getDashboardMetrics, appointments.loading]);  

  // Get today's schedule (may be loading independently)
  const todaySchedule = appointments.todayAppointments || [];
  
  // Quick stats (always available, may use cached data)
  const quickStats = {
    totalCustomers: customers.customers?.length || 0,
    activeServices: services.activeServices?.length || 0,
    todayAppointments: todaySchedule.length,
    upcomingCount: appointments.upcomingAppointments?.length || 0
  };

  const refreshMetrics = async () => {
    try {
      setMetricsLoading(true);
      setMetricsError(null);
      // Clear cache and fetch fresh data
      const dashboardMetrics = await appointments.getDashboardMetrics(true);
      setMetrics(dashboardMetrics);
    } catch (error) {
      console.error('Failed to refresh dashboard metrics:', error);
      setMetricsError(error.message);
    } finally {
      setMetricsLoading(false);
    }
  };

  return {
    // Data
    metrics,
    todaySchedule,
    quickStats,
    refreshMetrics,

    // Individual loading states - NO combined isLoading
    metricsLoading,
    appointmentsLoading: appointments.loading,
    customersLoading: customers.loading,
    servicesLoading: services.loading,
    
    // Individual error states
    metricsError,
    appointmentsError: appointments.error,
    customersError: customers.error,
    servicesError: services.error,
    
    // Utility to check if any section has errors
    hasAnyError: Boolean(appointments.error || customers.error || services.error || metricsError),
    
    // Refresh function
    refreshAll: () => {
      customers.clearError();
      services.clearError();
      appointments.clearError();
      setMetricsError(null);
    }
  };
}